import { EvesButton } from "@/common/components/atoms/button/button";
import { EvesTextbox } from "@/common/components/atoms/textbox/textbox";
import {
  Grid,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Button,
} from "@mui/material";
import Modal from "@mui/material/Modal";
import { Box } from "@mui/system";
import { CSSProperties, useState, useEffect } from "react";
import { Close } from "@mui/icons-material";
import {
  BillingInErrorAction,
  BillingInErrorStatus,
} from "@/common/enums/enums";
import {
  getBillingInErrorWithSessionID,
  resolveBillingError,
} from "@/services/invoices";
import store from "@/store";
import { setLoader, showNotification } from "@/store/app/reducer";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { IAppContextModel } from "@/store/app/types";
import { Loader } from "@/common/components/atoms/loader/loader";

interface IErrorDetailProps {
  onClose: () => void;
  isErrorDetailModalOpen: boolean;
  onUpdateSuccess?: () => void;
  sessionID: string;
}

const formatDate = (dateString?: string) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    const hours12 = date.getHours() % 12 || 12;
    const hours = String(hours12).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const ampm = date.getHours() >= 12 ? "PM" : "AM";
    return `${month}/${day}/${year}, ${hours}:${minutes}:${seconds}${ampm}`;
  } catch {
    return dateString;
  }
};

const style = {
  modalBox: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "550px",
    maxWidth: "90vw",
    bgcolor: "white",
    boxShadow:
      "0 20px 24px -4px rgba(16, 24, 40, 0.08), 0 8px 8px -4px rgba(16, 24, 40, 0.03)",
    borderRadius: "12px",
    p: 0,
    overflow: "hidden",
  },
  headerSection: {
    backgroundColor: "#F5F5F5",
    padding: "24px 24px 20px 24px",
    position: "relative",
  } as CSSProperties,
  contentSection: {
    padding: "24px",
  } as CSSProperties,
  header: {
    fontSize: "20px",
    fontWeight: "600",
    marginBottom: "4px",
    margin: 0,
  } as CSSProperties,
  sessionId: {
    fontSize: "14px",
    color: "#666",
    marginTop: "4px",
  } as CSSProperties,
  label: {
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "8px",
    display: "block",
  } as CSSProperties,
  lastUpdated: {
    fontSize: "14px",
    marginTop: "16px",
    color: "#666",
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
  } as CSSProperties,
  boldText: {
    fontWeight: "600",
    color: "#000",
  } as CSSProperties,
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
  } as CSSProperties,
  cancelButton: {
    flex: 1,
    height: "44px",
    fontSize: "14px",
    textTransform: "none",
    fontWeight: "500",
    backgroundColor: "white !important",
    borderRadius: "8px",
    color: "#333 !important",
    border: "1px solid #D0D0D0 !important",
    "&:hover": {
      backgroundColor: "#F5F5F5 !important",
      border: "1px solid #D0D0D0 !important",
    },
  },
  updateButton: {
    flex: 1,
    height: "44px",
    fontSize: "14px",
    textTransform: "none",
    fontWeight: "500",
    borderRadius: "8px",
  } as CSSProperties,
  closeIcon: {
    position: "absolute",
    right: "8px",
    top: "8px",
    cursor: "pointer",
  } as CSSProperties,
};

const ErrorDetailModal = (props: IErrorDetailProps) => {
  const [open, setOpen] = useState(props.isErrorDetailModalOpen);
  const [newStatus, setNewStatus] = useState<BillingInErrorAction>(
    BillingInErrorAction.IN_PROGRESS,
  );
  const [notes, setNotes] = useState<string>("");
  const [billingInErrorData, setBillingInErrorData] = useState<any>(null);
  const appContext: IAppContextModel = useSelector(
    (state: any) => state.appContext
  );

  // Get billing in error data
  const { refetch: getBillingInError } = useQuery(
    ["data", props.sessionID],
    () => {
      store.dispatch(setLoader(true));
      return getBillingInErrorWithSessionID(props.sessionID, {
        WithSite: true,
        WithCompany: true,
        WithUser: true,
      });
    },
    {
      enabled: false,
      onSettled: () => {
        store.dispatch(setLoader(false));
      },
      onSuccess: async (data: any) => {
        setBillingInErrorData(data);
        if (data?.latestRetryStatus?.action !== BillingInErrorStatus.IN_ERROR) {
          setNewStatus(data?.latestRetryStatus?.action);
          setNotes(data?.latestRetryStatus?.resolutionNote || "");
        }
      },
    },
  );

  useEffect(() => {
    getBillingInError();
  }, [props.sessionID]);

  const handleClose = () => {
    setOpen(false);
    setNewStatus(BillingInErrorAction.IN_PROGRESS);
    setNotes("");
    setBillingInErrorData(null);
    props.onClose();
  };

  const handleUpdateStatus = async () => {
    if (!props.sessionID) {
      store.dispatch(
        showNotification({
          showNotification: true,
          message: "Session ID is required",
          type: "error",
        }),
      );
      return;
    }

    store.dispatch(setLoader(true));
    try {
      const params = {
        timeStamp: new Date().toISOString(),
        resolutionNote: notes,
        action: newStatus,
      };

      const response: any = await resolveBillingError(props.sessionID, params);
      if (response?.status === "Success") {
        store.dispatch(
          showNotification({
            showNotification: true,
            message: response.message,
            type: "success",
          }),
        );

        handleClose();
        if (props.onUpdateSuccess) {
          props.onUpdateSuccess();
        }
      } else {
        const errorMessage = response?.data && typeof response?.data === "string" ? JSON.parse(response?.data)?.errorMessage : "Failed to process , Please check the logs and try again";

        store.dispatch(
          showNotification({
            showNotification: true,
            message: errorMessage,
            type: "error",
          }),
        );

        handleClose();
      }
    } catch (error: any) {
      store.dispatch(
        showNotification({
          showNotification: true,
          message: "Failed to process the status update, Please check the logs and try again",
          type: "error",
        }),
      );
    } finally {
      store.dispatch(setLoader(false));
    }
  };

  const statusOptions = [
    { value: BillingInErrorAction.IN_PROGRESS, label: "In Progress" },
    { value: BillingInErrorAction.RESOLVED, label: "Resolved" },
    // { value: BillingInErrorAction.RETRY, label: "Retry" },
  ];

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="manual-status-override-modal"
      aria-describedby="manual-status-override-description"
    >
      <Box sx={style.modalBox} data-cy="transaction-in-error-modal">
        {/* Header Section with Gray Background */}
        <div style={style.headerSection}>
          <IconButton
            onClick={handleClose}
            sx={style.closeIcon}
            data-cy="modal-close-icon"
          >
            <Close />
          </IconButton>
          <h3 style={style.header}>Manual Status Override</h3>
          <div style={style.sessionId}>
            Session ID: {billingInErrorData?.id || "-"}
          </div>
        </div>

        {/* Content Section */}
        <div style={style.contentSection}>
          <Grid container spacing={2} direction={"column"}>
            {/* New Status */}
            <Grid item xs={12}>
              <label style={style.label}>New Status</label>
              <FormControl fullWidth variant="outlined">
                <Select
                  value={newStatus || ""}
                  onChange={(e: any) => setNewStatus(e.target.value)}
                  displayEmpty
                  data-cy="new-status-select"
                  sx={{
                    height: "40px",
                    fontSize: "14px",
                  }}
                  renderValue={(selected) => {
                    if (!selected) return "";
                    const option = statusOptions.find(
                      (opt) => opt.value === selected,
                    );
                    return option ? option.label : "";
                  }}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <label style={style.label}>Notes</label>
              <EvesTextbox
                multiline
                rows={5}
                value={notes}
                onChange={(e: any) => setNotes(e.target.value)}
                placeholder="Enter notes..."
                variant="outlined"
                label=""
                data-cy="notes-textarea"
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: "14px",
                  },
                }}
              />
            </Grid>

            {/* Last Updated */}
            {(billingInErrorData?.latestRetryStatus?.timeStamp ||
              billingInErrorData?.latestRetryStatus?.userName) && (
                <Grid item xs={12}>
                  <div style={style.lastUpdated}>
                    <span>
                      Last Updated By:{" "}
                      <span style={style.boldText}>
                        {billingInErrorData?.latestRetryStatus?.userName || "-"}
                      </span>
                    </span>
                    <span>
                      Date & Time:{" "}
                      <span style={style.boldText}>
                        {billingInErrorData?.latestRetryStatus?.timeStamp
                          ? formatDate(
                            billingInErrorData?.latestRetryStatus?.timeStamp,
                          )
                          : "-"}
                      </span>
                    </span>
                  </div>
                </Grid>
              )}

            {/* Buttons */}
            <Grid item xs={12}>
              <div style={style.buttonContainer}>
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  sx={style.cancelButton}
                  data-cy="Error-cancel-button"
                >
                  Cancel
                </Button>
                <EvesButton
                  onClick={handleUpdateStatus}
                  variant="contained"
                  sx={{
                    ...style.updateButton,
                    "&.MuiButtonBase-root": {
                      fontSize: "14px",
                      borderRadius: "8px",
                    },
                  }}
                  data-cy="update-status-button"
                  disabled={!newStatus}
                >
                  Update Status
                </EvesButton>
              </div>
            </Grid>
          </Grid>
        </div>
        <Loader showLoader={appContext?.showLoader} />
      </Box>
    </Modal>
  );
};

export default ErrorDetailModal;
